<template>
  <section
    v-if="cityTags.length"
    class="sights-wrap"
  >
    <div>
      <PageWrap>
        <AttractionsTitle
          :title="sightsTitle"
          show-more="Смотреть все"
          :show-more-button="showMoreButton"
          :to="sightsUrl"
          @show-more-click="onViewAllClick"
        />
      </PageWrap>

      <div class="sights">
        <UiSlider
          :slides="cityTags"
          mousewheel
          arrows-always-visible
          :slides-per-view="slidesPerView"
          :space-between="16"
          :options="{
            slidesPerGroup: slidePerGroup,
            slidesOffsetAfter: sliderOffset,
            slidesOffsetBefore: sliderOffset,
          }"
        >
          <template #slide="{ slide }">
            <AttractionCard
              :attraction="slide"
              :analytics-params="getAttractionAnalytics(slide)"
              @select="$emit('select', $event)"
            />
          </template>
          <template #rightArrowIcon>
            <IconBase name="arrow" />
          </template>
          <template #leftArrowIcon>
            <IconBase
              name="arrow"
              class="mirror"
            />
          </template>
        </UiSlider>
      </div>

      <PageWrap>
        <div class="btn-wrap-more">
          <UiButtonV3
            v-if="showMoreButton && sightsUrl"
            :href="sightsUrl"
            :full-size="isMobile"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            text="Смотреть все"
            data-test="listing-sights-show-all"
            @clicked="onViewAllClick"
          />
        </div>
      </PageWrap>
    </div>
    <div
      id="sights"
      class="fake-anchor"
    />
  </section>
</template>

<script lang="ts" setup>
  import { useScreenSize } from '@experience/shared';
  import { UiSlider, UiButtonV3, UiButtonVariant, UiButtonColorMode } from '@experience/vue3-kit';
  import { computed, onMounted, onUnmounted, ref } from 'vue';

  import AttractionCard from '@/entities/aroundV2/AttractionCard.vue';
  import AttractionsTitle from '@/entities/aroundV2/AttractionsTitle.vue';
  import type { NearbyAttraction } from '@/shared/api/generated/api';
  import { trackEvent } from '@/shared/lib/analytics/compat';
  import IconBase from '@/shared/ui/IconBase.vue';
  import PageWrap from '@/shared/ui/PageWrap.vue';

  const props = withDefaults(
    defineProps<{
      attractions?: NearbyAttraction[];
      title?: string;
      sightsUrl?: string;
      analyticsBase?: Record<string, unknown>;
    }>(),
    {
      attractions: () => [],
      title: 'Что посмотреть',
      sightsUrl: '',
      analyticsBase: () => ({}),
    },
  );

  defineEmits<{
    (e: 'select', attraction: NearbyAttraction): void;
  }>();

  const { isMobile, isTablet } = useScreenSize();

  const cityTags = computed(() => props.attractions);
  const sightsTitle = computed(() => props.title);
  const sightsUrl = computed(() => props.sightsUrl);

  const sliderOffset = computed(() => {
    if (isTablet.value && !isMobile.value) {
      return 20;
    }

    if (isMobile.value) {
      return 24;
    }

    return 0;
  });

  type SlidesPerViewConfig = {
    min?: number;
    max?: number;
    slides: number | ((total: number) => number);
  };

  const slidesPerViewConfig: SlidesPerViewConfig[] = [
    { max: 359, slides: (total) => (total === 1 ? 1 : 1.45) },
    { min: 360, max: 439, slides: (total) => (total === 1 ? 1.05 : 1.62) },
    { min: 440, max: 679, slides: (total) => (total < 3 ? 2 : 2.01) },
    {
      min: 680,
      max: 767,
      slides: (total) => {
        if (total <= 2) return 2;
        if (total === 3) return 3;
        return 3.2;
      },
    },
    {
      min: 768,
      max: 799,
      slides: (total) => {
        if (total <= 2) return 2;
        if (total === 3) return 3;
        return 3.6;
      },
    },
    { min: 800, max: 859, slides: (total) => (total < 5 ? 4 : 4.15) },
    { min: 860, max: 919, slides: (total) => (total < 5 ? 4 : 4.3) },
    {
      min: 920,
      max: 1023,
      slides: (total) => {
        if (total < 5) return 4;
        if (total === 5) return 5;
        return 5.2;
      },
    },
    { min: 1024, max: 1199, slides: (total) => (total < 5 ? 4 : 5) },
    {
      min: 1200,
      slides: (total) => {
        if (total < 5) return 4;
        if (total === 5) return 5;
        return 6;
      },
    },
  ];

  const getSlidesPerView = (total: number): number => {
    const config = slidesPerViewConfig.find(({ min, max }) => {
      if (min && max)
        return window.matchMedia(`(min-width: ${String(min)}px) and (max-width: ${String(max)}px)`).matches;
      if (min) return window.matchMedia(`(min-width: ${String(min)}px)`).matches;
      if (max) return window.matchMedia(`(max-width: ${String(max)}px)`).matches;
      return false;
    });

    if (!config) return 4;
    return typeof config.slides === 'function' ? config.slides(total) : config.slides;
  };

  const slidesPerView = ref(0);
  const slidesTotalCount = computed(() => cityTags.value.length);
  const slidePerGroup = computed(() => (isMobile.value || isTablet.value ? 1 : slidesPerView.value));
  const showMoreButton = computed(() => slidesTotalCount.value > slidesPerView.value);

  const updateSlidesPerView = () => {
    slidesPerView.value = getSlidesPerView(slidesTotalCount.value);
  };

  const getAttractionAnalytics = (attraction: NearbyAttraction): Record<string, unknown> => ({
    ...props.analyticsBase,
    subject_geo_id: attraction.id,
    tag_id: attraction.tag_id,
    tag_name: attraction.name,
  });

  const onViewAllClick = async () => {
    await trackEvent('around:sights:view-all.click', props.analyticsBase);
  };

  onMounted(() => {
    updateSlidesPerView();
    window.addEventListener('resize', updateSlidesPerView);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateSlidesPerView);
  });
</script>

<style scoped lang="scss">
  .sights-wrap {
    position: relative;
    padding: 40px 0 0;
    overflow: hidden;

    .btn-wrap-more {
      display: none;
      margin-top: 24px;

      :deep(.button) {
        width: 100%;
      }

      @include mobile {
        margin-top: 16px;
      }

      @include tablet {
        display: block;
      }
    }

    @include tablet {
      padding: 32px 0 0;
    }
  }

  .mirror {
    transform: scaleX(-1);
  }

  .sights {
    width: 100vw;
    min-width: var(--wrap-min-width);
    max-width: var(--wrap-max-width);
    padding: 0 var(--wrap-offset);
    margin: 0 auto;

    @include tablet {
      box-sizing: border-box;
      padding: 0;
    }
  }

  :deep(.btn-slider.btn-prev),
  :deep(.btn-slider.btn-next) {
    top: 50% !important;
  }

  :deep(.btn-slider) {
    @include tablet {
      display: none;
    }
  }
</style>
